import { useState, useEffect } from "react";
import { useLeague } from "./LeagueLayout";
import { BookOpen, Users, ShieldCheck, ChevronDown, ChevronRight, PlayCircle } from "lucide-react";
import VideoTour from "../../components/VideoTour";

/**
 * Iter 69.7: Guida Onboarding — Admin & Manager in un unico file.
 * Accessibile da menu lega. Sezioni collapsibili per rapida consultazione.
 */
export default function LeagueGuida() {
  const { isAdmin, league } = useLeague();
  const [tab, setTab] = useState(isAdmin ? "admin" : "manager");
  const [tourOpen, setTourOpen] = useState(false);
  const [tourRole, setTourRole] = useState(isAdmin ? "admin" : "manager");

  // Auto-play tour la prima volta (persistente per lega + user + role)
  useEffect(() => {
    if (!league?.league_id) return;
    const role = isAdmin ? "admin" : "manager";
    const key = `mfb_tour_shown_${league.league_id}_${role}`;
    if (!localStorage.getItem(key)) {
      setTourRole(role);
      setTourOpen(true);
      localStorage.setItem(key, new Date().toISOString());
    }
  }, [isAdmin, league?.league_id]);

  const openTour = (role) => {
    setTourRole(role);
    setTourOpen(true);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="guida-page">
      {tourOpen && <VideoTour role={tourRole} onClose={() => setTourOpen(false)} onSkip={() => setTourOpen(false)}/>}
      <div className="rounded-3xl border border-amber-500/40 bg-gradient-to-br from-slate-900 to-amber-950/20 p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
            <BookOpen className="text-amber-400" size={22}/>
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] tracking-[0.3em] text-amber-400 uppercase font-bold">Onboarding & Guida</div>
            <h1 className="text-3xl font-black text-white mt-0.5">Come funziona MyFantabusiness</h1>
            <p className="text-sm text-slate-400 mt-1">Struttura, fasi, calcoli. La stessa guida contiene ADMIN e MANAGER.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            {isAdmin && (
              <button onClick={() => openTour("admin")} data-testid="open-video-tour-admin"
                className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-700 text-white font-black text-xs hover:brightness-110 shadow-lg shadow-cyan-500/30">
                <PlayCircle size={14}/> Tour Admin · 60s
              </button>
            )}
            <button onClick={() => openTour("manager")} data-testid="open-video-tour-manager"
              className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white font-black text-xs hover:brightness-110 shadow-lg shadow-emerald-500/30">
              <PlayCircle size={14}/> Tour Manager · 60s
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <TabBtn active={tab==="admin"} onClick={() => setTab("admin")} icon={ShieldCheck} label="Guida Admin" testid="tab-admin"/>
        <TabBtn active={tab==="manager"} onClick={() => setTab("manager")} icon={Users} label="Guida Manager" testid="tab-manager"/>
      </div>

      {tab === "admin" ? <AdminGuide/> : <ManagerGuide/>}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-black text-sm uppercase tracking-wider border transition-colors ${active ? "bg-amber-500/20 border-amber-500/60 text-amber-200" : "bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-600"}`}>
      <Icon size={14}/> {label}
    </button>
  );
}

function Section({ title, badge, defaultOpen = false, children, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 p-4 hover:bg-slate-800/40 transition-colors text-left">
        {open ? <ChevronDown size={16} className="text-slate-500"/> : <ChevronRight size={16} className="text-slate-500"/>}
        <span className="font-black text-white text-lg flex-1">{title}</span>
        {badge && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 font-bold">{badge}</span>}
      </button>
      {open && <div className="px-4 pb-4 text-sm text-slate-300 leading-relaxed space-y-3">{children}</div>}
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3 items-start pt-2">
      <span className="w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 font-black text-xs flex items-center justify-center shrink-0 mt-0.5">{n}</span>
      <div className="flex-1">
        {title && <div className="font-bold text-white mb-1">{title}</div>}
        <div className="text-slate-300 text-sm">{children}</div>
      </div>
    </div>
  );
}

function AdminGuide() {
  return (
    <div className="space-y-3" data-testid="admin-guide-content">
      <Section title="🗺️ Struttura organizzativa della lega" defaultOpen testid="section-struttura">
        <p>La lega è organizzata in fasi che riflettono l&apos;anno fantacalcistico:</p>
        <ul className="list-disc list-inside text-slate-400 text-xs space-y-1 ml-2">
          <li><strong className="text-slate-200">Fase 0-1</strong>: Setup e Import Rose Iniziali (Q.I./Q.A. dal listone, salary=0)</li>
          <li><strong className="text-slate-200">Fase 2</strong>: Pre-Riparazione (mercato chiuso, rose congelate)</li>
          <li><strong className="text-slate-200">Fase 3</strong>: Riparazione (mercato aperto, trasferimenti fantasy)</li>
          <li><strong className="text-slate-200">Fase 4-7</strong>: Campionato (gioco in corso, rivalutazioni Q.A. via import)</li>
          <li><strong className="text-slate-200">Fase 8</strong>: Pre-Asta Furti (i manager scelgono chi rinnovare → nascono gli STIPENDI)</li>
          <li><strong className="text-slate-200">Fase 9</strong>: Asta Furti (asta dei giocatori liberati)</li>
          <li><strong className="text-slate-200">Fase 10+</strong>: Nuova stagione</li>
        </ul>
        <p>Ogni fase è avanzata dall&apos;admin dalla tile <strong className="text-amber-300">🎯 Decisioni Admin</strong>. Prima di avanzare puoi creare uno <em>snapshot milestone</em> (T0-T5).</p>
      </Section>

      <Section title="📥 Caricamento Rose e Listone (Fase 0)" testid="section-import">
        <p>Vai a <strong className="text-amber-300">Decisioni Admin → Import & Aggiornamenti</strong>. Ci sono 3 sezioni distinte:</p>
        <Step n={1} title="Quotazioni Listone">
          Scarica il file <em>Quotazioni_Fantacalcio_Stagione_XX_YY.xlsx</em> da Fantacalcio.it e importalo. Aggiorna Q.I., Q.A., FVM di tutti i giocatori in tutte le rose. NON tocca proprietà e stipendi.
        </Step>
        <Step n={2} title="Rose Fantacalcio (CSV)">
          Carica il file CSV di Fantacalcio.it con le rose delle 10 squadre. Il sistema fa match per fanta_id + nome fuzzy. Crea automaticamente i giocatori mancanti (senza fanta_id inizialmente).
        </Step>
        <Step n={3} title="Post-Calendario (XLSX)">
          Solo dopo che le rose CSV sono state importate. Contiene prezzi pagati, salary, ecc. Aggiorna i dati economici. Prima genera snapshot pre-import per rollback.
        </Step>
        <p className="text-xs text-amber-300">⚠️ Ordine consigliato: Quotazioni → Rose CSV → Post-Calendario. Ogni step crea uno snapshot automatico.</p>
      </Section>

      <Section title="🩺 Salute Rosa e collegamento fanta_id" testid="section-salute">
        <p>Vai a <strong className="text-amber-300">Decisioni Admin → 🩺 Salute Rosa</strong>. Qui gestisci i giocatori &quot;Da verificare&quot; (senza fanta_id o con nome non trovato).</p>
        <ul className="list-disc list-inside text-slate-400 text-xs space-y-1 ml-2">
          <li><strong>🪄 Auto-Attach Fanta ID</strong>: un click risolve automaticamente tutti i giocatori con match ≥85% dal listone attuale o dall&apos;archivio storico multi-stagione.</li>
          <li><strong>🤖 Risolvi con AI</strong>: Claude Sonnet 5 propone il match più probabile. Consuma credito AI.</li>
          <li><strong>♻️ Riconcilia Listone</strong>: riporta a LISTED_ACTIVE i giocatori che erano fuori listone ma sono rientrati. Refresha Q.I./Q.A. dal listone corrente.</li>
          <li><strong>🔧 Assegna manualmente</strong>: se un nome è troppo storpiato per il fuzzy, cerca a mano su listone + archivio.</li>
        </ul>
      </Section>

      <Section title="💰 Metodo di calcolo — Formule unificate" testid="section-formule">
        <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-3 space-y-2 font-mono text-xs">
          <div><strong className="text-emerald-300">Valore Rivalutato</strong> = prezzo_pagato × (Q.A. ÷ Q.I.)</div>
          <div><strong className="text-cyan-300">Base Asta Furti</strong> = Valore_Rivalutato × (1 + bonus_stipendio_pct/100)</div>
          <div><strong className="text-amber-300">Crediti Disponibili</strong> = budget − monte_stipendi</div>
          <div><strong className="text-purple-300">ROI</strong> = (Valore_Rivalutato − prezzo_pagato) ÷ prezzo_pagato × 100</div>
        </div>
        <p className="text-xs text-slate-400">Il salary emerge SOLO in fase 8+ dopo che i manager fanno le scelte pre-asta furti. Prima di allora salary=0.</p>
      </Section>

      <Section title="📸 Milestone e Snapshot" testid="section-milestone">
        <p>Le milestone (T0-T5) sono fotografie dello stato della lega per confronti nel tempo:</p>
        <ul className="list-disc list-inside text-slate-400 text-xs space-y-1 ml-2">
          <li><strong>T0 Rose Iniziali</strong> · <strong>T1 Pre-Riparazione</strong> · <strong>T2 Post-Riparazione</strong></li>
          <li><strong>T3 Fine Campionato</strong> · <strong>T4 Pre-Asta Furti</strong> · <strong>T5 Post-Asta Furti</strong></li>
        </ul>
        <p>Ogni avanzamento fase apre un dialog che suggerisce la milestone appropriata. Puoi confrontare due milestone nella tile <strong className="text-amber-300">📸 Milestone</strong> per vedere delta budget/rose.</p>
      </Section>

      <Section title="♻️ Backup e Rollback" testid="section-rollback">
        <p>Ogni import e ogni operazione bulk genera automaticamente uno <em>snapshot pre-operazione</em>.</p>
        <p>Trovi lo storico degli snapshot in <strong className="text-amber-300">Decisioni Admin → Backup & Ripristino Rose</strong>: puoi ripristinare qualsiasi punto pre-import in un click.</p>
        <p className="text-xs text-amber-300">💡 Suggerimento: prima di operazioni rischiose crea uno snapshot manuale con nome parlante (es. &quot;pre_asta_2025&quot;).</p>
      </Section>

      <Section title="👥 Gestione manager e squadre" testid="section-manager">
        <p>Vai a <strong className="text-amber-300">Decisioni Admin → 👤 Utenti & Squadre</strong>:</p>
        <ul className="list-disc list-inside text-slate-400 text-xs space-y-1 ml-2">
          <li>Invia inviti via codice lega (i manager si iscrivono con Emergent Google Auth).</li>
          <li>Assegna una squadra a ogni manager.</li>
          <li>Nomina co-admin (utenti con permessi identici all&apos;admin principale).</li>
        </ul>
      </Section>

      <Section title="🎨 Sponsor" testid="section-sponsor">
        <p>La lega ha 20 sponsor piattaforma pre-configurati (Ex &amp; Breakfast, KillHire, SporcoPulito, ecc.). I manager possono candidarsi a uno sponsor dalla loro dashboard.</p>
        <p>In <strong className="text-amber-300">Decisioni Admin → Sponsor</strong> vedi tutti i contratti in essere, chi ha raggiunto l&apos;obiettivo, e chi ha ricevuto il bonus.</p>
      </Section>

      <Section title="💳 Piano lega e Stripe" testid="section-plan">
        <p>Ogni lega ha uno stato: <strong>pending</strong> (creata, non attivata) o <strong>active</strong> (pagamento ricevuto).</p>
        <p>L&apos;admin può attivare la lega da <strong className="text-amber-300">Decisioni Admin → Piano & Pagamento</strong> tramite Stripe Checkout (pagamento unico). Alla ricezione del webhook la lega passa a active e sblocca tutte le funzionalità.</p>
      </Section>
    </div>
  );
}

function ManagerGuide() {
  return (
    <div className="space-y-3" data-testid="manager-guide-content">
      <Section title="👋 Benvenuto Manager" defaultOpen testid="section-welcome-mgr">
        <p>Sei un magnate del fantacalcio in MyFantabusiness. La tua squadra è la tua azienda: gestisci rose, budget, sponsor, e affronti aste stagionali.</p>
      </Section>

      <Section title="🏠 Dove trovi cosa" testid="section-nav-mgr">
        <ul className="list-disc list-inside text-slate-400 text-xs space-y-1 ml-2">
          <li><strong>Dashboard</strong>: panoramica della tua squadra, crediti disponibili, prossime scadenze.</li>
          <li><strong>Rosa</strong>: la tua lista giocatori con Q.I./Q.A., valore rivalutato, ROI.</li>
          <li><strong>Rinnovi</strong>: la lista dei rinnovi pendenti (fase 8+).</li>
          <li><strong>Confronto Rose</strong>: vista sinottica di tutte le squadre della lega.</li>
          <li><strong>Asta</strong>: giocatori liberi da comprare in asta (fase 9+).</li>
          <li><strong>Sponsor</strong>: le proposte sponsor a cui puoi candidarti.</li>
          <li><strong>Storico</strong>: la carriera di ogni giocatore (click sul nome apre Timeline multi-stagione).</li>
        </ul>
      </Section>

      <Section title="💰 Come funzionano i crediti" testid="section-crediti-mgr">
        <p>Ogni squadra parte con un budget iniziale (tipicamente 500cr) che si evolve stagione dopo stagione.</p>
        <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-3 space-y-1 font-mono text-xs">
          <div>Crediti Disponibili = <strong className="text-amber-300">budget − monte_stipendi</strong></div>
          <div>Valore rosa = Σ (prezzo_pagato × Q.A./Q.I.)</div>
        </div>
        <p className="text-xs text-slate-400">Il monte stipendi emerge solo in fase 8+ quando fai le scelte pre-asta furti. Prima di allora conta solo il budget.</p>
      </Section>

      <Section title="🔄 Rinnovi e Asta Furti" testid="section-rinnovi-mgr">
        <p>In fase 8 devi decidere:</p>
        <Step n={1} title="Trattieni">Rinnova il giocatore pagando lo stipendio (nasce il salary che ridurrà i crediti disponibili).</Step>
        <Step n={2} title="Libera">Il giocatore va all&apos;asta furti (fase 9). Ricevi crediti_recuperati come compensazione parziale.</Step>
        <Step n={3} title="Asta Furti">In fase 9 puoi rilanciare per giocatori liberati da altri. Base d&apos;asta = valore_rivalutato del vecchio proprietario × (1 + bonus_stipendio%).</Step>
      </Section>

      <Section title="📊 Timeline giocatori (novità)" testid="section-timeline-mgr">
        <p>Clicca su qualsiasi nome giocatore (Rosa, Rinnovi, Asta, Confronto) per aprire la <strong className="text-purple-300">Timeline Multi-Stagione</strong>: vedi Q.I./Q.A./FVM del giocatore in tutte le stagioni tracciate, con delta interstagione.</p>
        <p className="text-xs text-slate-400">Ti aiuta a capire se un giocatore è in ascesa o discesa prima di decidere trattieni/libera.</p>
      </Section>

      <Section title="🎨 Sponsor: candidature e obiettivi" testid="section-sponsor-mgr">
        <p>Dal menu <strong>Sponsor</strong> vedi tutte le proposte disponibili. Ogni sponsor ha:</p>
        <ul className="list-disc list-inside text-slate-400 text-xs space-y-1 ml-2">
          <li><strong>Upfront credits</strong>: crediti ricevuti alla firma.</li>
          <li><strong>Objective</strong>: cosa devi ottenere (es. classifica top 3, X gol da un attaccante).</li>
          <li><strong>Bonus</strong>: crediti se raggiungi l&apos;obiettivo.</li>
        </ul>
        <p>Candidati liberamente: l&apos;admin approva la trattativa.</p>
      </Section>

      <Section title="🆘 Se qualcosa non torna" testid="section-help-mgr">
        <p>Se noti dati errati (Q.A. non aggiornata, giocatore mancante, credito sballato), contatta l&apos;admin della lega: può usare la <strong>Salute Rosa</strong> o il <strong>Backup &amp; Ripristino</strong> per correggere in un click.</p>
      </Section>
    </div>
  );
}
